import { reaction } from 'mobx';
import { userSettingsStore } from '../../../store/userSettingsStore';
import {
  AnimationConfigs,
  PowerOfGodsSoundsNames,
  SymbolSoundMap,
} from '../powerOfGodsCore/constants/globalConstants';
import { ReelStore } from '../powerOfGodsStores/reelStore';
import { ReelView } from '../powerOfGodsViewers/reeView';
import { reelParams, symbolEnum } from '../powerOfGodsCore/constants/reelConstants';
import { soundService } from '../../../services/baseSoundService/soundService';

export class ReelController {
  store = null;

  view = null;

  constructor(parentContainer, winContainer, initialSymbol) {
    this.store = new ReelStore();
    this.view = new ReelView(parentContainer, winContainer);
    this.view.onInit(() => {
      this.initReel(this.store.isInBonusScene, initialSymbol);
    });
    this.setupReactions();
  }

  setupReactions() {
    reaction(
      () => this.store.rotating,
      (rotating) => rotating && this.startSpin()
    );

    reaction(
      () => this.store.isForceStop,
      (force) => force && this.store.rotating && this.turboStop()
    );

    reaction(
      () => this.store.responseReady,
      (ready) => ready && userSettingsStore.isTurboOn && this.turboStop()
    );

    reaction(
      () => userSettingsStore.isTurboOn,
      () => {
        Object.assign(this.view, this.view.calculatePositionsDuration(userSettingsStore.isTurboOn));
      }
    );

    reaction(
      () => this.store.reelInitComplete,
      (InitComplete) => {
        if (InitComplete) {
          this.store.setCanStop(false);
          this.store.resetWhenReelStop();
        }
      }
    );
    reaction(
      () => this.store.isInBonusScene,
      (isInBonusScene) => isInBonusScene && this.store.setCanStop(false)
    );
  }

  async initReel(isInBonusScene, initialSymbol) {
    await this.view.addIcons(isInBonusScene, initialSymbol);
    this.store.setReelInitComplete(true);
  }

  startSpin() {
    this.store.setCanRotate(false);
    this.store.setFirstLoop(true);
    this.loop();
  }

  loop() {
    const animationParams = this.calculateAnimationParams();
    this.view.playLoop(animationParams, () => this.onLoopRepeat(), this.store.isInBonusScene);
  }

  onLoopRepeat() {
    if (this.store.canStop) {
      if (this.store.canStretch && !this.store.stretchStopCalled) {
        this.stopStretchAnimation();
      } else if (!this.store.canStretch) {
        this.stopAnimation();
        return;
      }
    }
    soundService.play(PowerOfGodsSoundsNames.ReelLoop);
    this.view.updateLoopSymbols(this.visibleSlice(), this.store.isInBonusScene);
    if (this.store.firstLoop) {
      this.store.setFirstLoop(false);
    }
  }

  stopAnimation() {
    if (this.store.stopCalled) return;
    this.store.setStopCalled(true);
    const animationParams = this.calculateAnimationParams();
    this.view.playStop(
      animationParams,
      () => {
        this.switchSoundForResultSymbol();
        this.store.setStopComplete(true);
        soundService.play(PowerOfGodsSoundsNames.ReelStop);
      },
      userSettingsStore.isTurboOn || this.store.isForceStop,
      this.store.responseData
    );
  }

  switchSoundForResultSymbol() {
    // if (this.store.responseData === symbolEnum.medusa) {
    //   return;
    // }
    const soundKey = this.store.responseData;
    const soundName = SymbolSoundMap[soundKey];
    if (soundName && soundKey !== 0) {
      soundService.play(soundName);
    }
  }

  stopStretchAnimation() {
    if (this.store.stretchStopCalled) return;
    this.store.setStretchStopCalled(true);
    const animationParams = this.calculateAnimationParams();
    this.view.playStretchStop(
      animationParams,
      () => {
        this.store.setStopComplete(true);
        soundService.play(PowerOfGodsSoundsNames.ReelStop);
      },
      this.store.responseData
    );
  }

  turboStop() {
    this.stopAnimation();
  }

  async onStopComplete() {
    this.setReelStopComplete();
    await this.view.startWinAnimation();
    this.store.setCanRotate(true);
  }

  setReelStopComplete() {
    this.store.resetWhenReelStop();
  }

  skipWinAnimation() {
    return this.view.skipWinActions();
  }

  changeBonusModeActiveState(state) {
    this.store.setIsInBonusScene(state);
    state ? this.view.changeToBonusMode() : this.view.changeToNormalMode();
  }

  initFromResponse(resultSymbol) {
    const data = resultSymbol;
    const enumValues = Object.values(symbolEnum);
    if (!enumValues.includes(data)) return;
    this.store.setResponseData(data);
    this.store.setResponseReady(true);
  }

  calculateAnimationParams() {
    const startY = reelParams.finalStopPositionY;
    const rowsH = reelParams.visibleRows * 3 * reelParams.iconsHeight;
    const isTurbo = userSettingsStore.isTurboOn || this.store.isForceStop;
    const isInBonus = this.store.isInBonusScene;
    const { loopDuration, stretchDuration } = reelParams;

    const base = {
      posStart: startY - rowsH,
      posEnd: startY + rowsH,
      duration: loopDuration,
      stopDuration: loopDuration,
      stretchDuration: stretchDuration,
      posEndStop: startY,
    };
    if (isTurbo) {
      return {
        ...base,
        stopDuration: loopDuration / 5,
        duration: isInBonus ? AnimationConfigs.bonusLoopStepDuration : loopDuration / 2,
        ease: isInBonus ? AnimationConfigs.bonusEase : AnimationConfigs.stopTurbo,
      };
    }

    return {
      ...base,
      ease: isInBonus ? AnimationConfigs.bonusEase : AnimationConfigs.stopNormal,
      stopDuration: isInBonus ? AnimationConfigs.bonusLoopStepDuration * 1.2 : loopDuration,
      duration: isInBonus ? AnimationConfigs.bonusLoopStepDuration : loopDuration,
    };
  }

  visibleSlice() {
    const start = 0;
    const end = this.view.iconsInReel - 1;
    return [start, end];
  }

  dispose() {
    this.disposers.forEach((disposer) => disposer());
    this.disposers = [];
  }
}
